import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Avatar from '../common/Avatar';
import Button from '../common/Button';
import {
  LayoutDashboard,
  Store,
  Users,
  CreditCard,
  ClipboardCheck,
  TrendingUp,
  Settings,
  ShoppingCart,
  Receipt,
  Tag,
} from 'lucide-react';

const Navbar: React.FC = () => {
  const { user, signOut, isAdmin, isAssociate } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  // Associates have their own interface, redirect if they somehow reach here
  if (isAssociate) {
    return null;
  }

  const handleLogout = () => {
    signOut();
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleProfile = () => {
    setIsProfileOpen(!isProfileOpen);
  };

  return (
    <nav className="bg-white shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <Link to="/" className="flex items-center">
              <div className="flex-shrink-0 flex items-center">
                <div className="h-10 w-10 bg-gradient-to-r from-green-600 to-green-800 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-xl">HMG</span>
                </div>
                <span className="ml-2 text-xl font-bold text-gray-900 hidden lg:block whitespace-nowrap">
                  Cartão HMG
                </span>
              </div>
            </Link>

            <div className="hidden md:ml-8 md:flex md:space-x-2 lg:space-x-4">
              <Link
                to="/dashboard"
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
              >
                <LayoutDashboard className="w-4 h-4 mr-2" />
                Dashboard
              </Link>
              <Link
                to="/parceiros"
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
              >
                <Store className="w-4 h-4 mr-2" />
                Parceiros
              </Link>
              <Link
                to="/categorias"
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
              >
                <Tag className="w-4 h-4 mr-2" />
                Categorias
              </Link>
              <Link
                to="/associados"
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
              >
                <Users className="w-4 h-4 mr-2" />
                Associados
              </Link>
              <Link
                to="/planos"
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Planos
              </Link>
              <Link
                to="/associados/aprovacoes"
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
              >
                <ClipboardCheck className="w-4 h-4 mr-2" />
                Aprovações
              </Link>
              {/* <Link
                to="/utilizar-beneficio"
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Utilizar Benefício
              </Link> */}
              <Link
                to="/financeiro"
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Financeiro
              </Link>
              <Link
                to="/contas-receber"
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
              >
                <Receipt className="w-4 h-4 mr-2" />
                Contas a Receber
              </Link>
              {isAdmin && (
                <Link
                  to="/configuracoes"
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Configurações
                </Link>
              )}
            </div>
          </div>

          <div className="flex items-center">
            <div className="hidden md:flex md:items-center md:space-x-4">
              <span className="text-sm text-gray-700 whitespace-nowrap max-w-[200px] truncate">
                Olá, <span className="font-semibold">{user?.name}</span>
              </span>

              <div className="relative">
                <button
                  onClick={toggleProfile}
                  className="flex items-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-full"
                >
                  <Avatar name={user?.name || ''} size="md" />
                </button>

                {isProfileOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setIsProfileOpen(false)}
                    />
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-20 border border-gray-200">
                      <div className="px-4 py-2 border-b border-gray-200">
                        <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                        <p className="text-xs text-gray-500">{user?.email}</p>
                      </div>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        Sair
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center md:hidden">
              <button
                onClick={toggleMenu}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 transition-colors"
              >
                <span className="sr-only">Abrir menu</span>
                {isMenuOpen ? (
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                ) : (
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {isMenuOpen && (
        <div className="md:hidden border-t border-gray-200">
          <div className="px-2 pt-2 pb-3 space-y-1">
            <Link
              to="/dashboard"
              className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              <LayoutDashboard className="w-5 h-5 mr-3" />
              Dashboard
            </Link>
            <Link
              to="/parceiros"
              className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              <Store className="w-5 h-5 mr-3" />
              Parceiros
            </Link>
            <Link
              to="/categorias"
              className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              <Tag className="w-5 h-5 mr-3" />
              Categorias
            </Link>
            <Link
              to="/associados"
              className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              <Users className="w-5 h-5 mr-3" />
              Associados
            </Link>
            <Link
              to="/planos"
              className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              <CreditCard className="w-5 h-5 mr-3" />
              Planos
            </Link>
            <Link
              to="/associados/aprovacoes"
              className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              <ClipboardCheck className="w-5 h-5 mr-3" />
              Aprovações Pendentes
            </Link>
            <Link
              to="/utilizar-beneficio"
              className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              <ShoppingCart className="w-5 h-5 mr-3" />
              Utilizar Benefício
            </Link>
            <Link
              to="/financeiro"
              className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              <TrendingUp className="w-5 h-5 mr-3" />
              Financeiro
            </Link>
            <Link
              to="/contas-receber"
              className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              <Receipt className="w-5 h-5 mr-3" />
              Contas a Receber
            </Link>
            {isAdmin && (
              <Link
                to="/configuracoes"
                className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                <Settings className="w-5 h-5 mr-3" />
                Configurações
              </Link>
            )}
          </div>
          <div className="pt-4 pb-3 border-t border-gray-200">
            <div className="flex items-center px-4">
              <Avatar name={user?.name || ''} size="md" />
              <div className="ml-3">
                <div className="text-base font-medium text-gray-900">{user?.name}</div>
                <div className="text-sm font-medium text-gray-500">{user?.email}</div>
              </div>
            </div>
            <div className="mt-3 px-2">
              <Button
                variant="danger"
                onClick={handleLogout}
                className="w-full"
              >
                Sair
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
